import { nanoid } from "nanoid"
import { jobShopTag, pageTag, tenantTag } from "@/utils/cacheTags"
import { jobPageTemplateCacheItemKey } from "@/utils/cacheKeys"
import Logger from "@/utils/Logger"

export async function useJobPageTemplateData(templateId: string | null) {
  const { $api } = useNuxtApp()
  const { t } = useI18n()
  const { tenantId, jobShopId, acceptLanguage } = useHeaderStore()
  const { dataCacheTtl, dataCacheStaleTtl } = useRuntimeConfig()

  const requestId = nanoid()
  const contextString = `job page template data for templateId: "${templateId}", tenantId: "${tenantId}" and jobShopId: "${jobShopId}"`

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  const cacheKey = jobPageTemplateCacheItemKey(templateId)
  const {
    data: jobPageTemplate,
    status: jobPageTemplateStatus,
    error: jobPageTemplateError,
  } = await useAsyncData(cacheKey, () =>
    // Stale-if-error (JET-2212) on upstream unavailability (not 4xx); tags include the resolved page id.
    useDataCacheWithStale(cacheKey, () => $api.jobPageTemplate.getJobPageTemplateById(tenantId, jobShopId, acceptLanguage, templateId), {
      maxAge: dataCacheTtl,
      staleIfError: dataCacheStaleTtl,
      requestId,
      tags: data => [tenantTag(tenantId), jobShopTag(jobShopId), pageTag(data.id)],
    }))

  if (import.meta.server) {
    if (!jobPageTemplateError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const errorMessage = jobPageTemplateError.value?.data?.errors?.[0]?.message || jobPageTemplateError.value?.data?.description || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage}\n\n`, jobPageTemplateError.value)
    }
  }

  if (jobPageTemplateError.value) {
    if (jobPageTemplateError.value.statusCode === 404) {
      throw createError({
        statusCode: 404,
        statusMessage: t("error-messages.page-not-found"),
      })
    }
    else {
      throw createError({
        statusCode: jobPageTemplateError.value.statusCode,
        statusMessage: t("error-messages.general"),
      })
    }
  }

  return { jobPageTemplate, jobPageTemplateStatus, jobPageTemplateError }
}
